"use client";

import { useCallback, useDeferredValue, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useThreadsListQuery } from "@/components/chats/useThreadsListQuery";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import type { ThreadItem } from "@/components/custom/contexts/threadContext";
import { appendLocParam } from "@/lib/i18n";
import { cn } from "@/lib/utils";

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function HighlightedTitle({
  title,
  query,
  locale,
}: {
  title: string;
  query: string;
  locale: string;
}) {
  const trimmed = query.trim();
  if (!trimmed) return <>{title}</>;
  const lowerTitle = title.toLocaleLowerCase(locale);
  const lowerQuery = trimmed.toLocaleLowerCase(locale);
  const idx = lowerTitle.indexOf(lowerQuery);
  if (idx === -1) return <>{title}</>;
  return (
    <>
      {title.slice(0, idx)}
      <mark className="rounded-sm bg-[oklch(0.92_0.08_90)] px-0.5 text-foreground dark:bg-[oklch(0.45_0.10_90)] dark:text-white">
        {title.slice(idx, idx + trimmed.length)}
      </mark>
      {title.slice(idx + trimmed.length)}
    </>
  );
}

function formatPaletteTimestamp(
  iso: string | undefined,
  t: (key: string) => string,
): string {
  if (!iso) return t("chatsPage.sectionOlder");
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return t("chatsPage.sectionOlder");

  const now = new Date();
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  if (date >= today) return t("chatsPage.sectionToday");
  if (date >= yesterday) return t("chatsPage.sectionYesterday");
  if (date >= weekAgo) return t("chatsPage.sectionThisWeek");
  return t("chatsPage.sectionOlder");
}

const PAGE_SIZE = 10;

type SearchPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function SearchPalette({ open, onOpenChange }: SearchPaletteProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "cs";
  const navigate = useNavigate();
  const routeContext = useRouteContext();

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const { items, isLoading } = useThreadsListQuery({
    q: deferredQuery,
    searchInContent: false,
    pageSize: PAGE_SIZE,
    caseId: routeContext.launchCaseId ?? routeContext.caseId,
  });

  // Cap at PAGE_SIZE — the hook can hold prior pages from a previous query,
  // but the palette only ever renders the top slice.
  const visibleItems = items.slice(0, PAGE_SIZE);

  // Reset query and cursor whenever the palette opens, so the user always
  // sees a clean state instead of stale typing from the previous session.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Clamp the cursor whenever the result list changes — keystrokes can shrink
  // it past the previous activeIndex.
  useEffect(() => {
    setActiveIndex((i) => {
      if (visibleItems.length === 0) return 0;
      return Math.min(i, visibleItems.length - 1);
    });
  }, [visibleItems.length]);

  // Keep the active row in view when the user arrows past the fold.
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.querySelector<HTMLElement>(
      `[data-palette-row-index="${activeIndex}"]`,
    );
    row?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleSelect = useCallback(
    (item: ThreadItem) => {
      const caseIdSegment = item.caseId ?? "default";
      navigate(appendLocParam(`/${caseIdSegment}/${item.remoteId}`));
      onOpenChange(false);
    },
    [navigate, onOpenChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, visibleItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        if (visibleItems.length === 0) return;
        e.preventDefault();
        const target = visibleItems[activeIndex];
        if (target) handleSelect(target);
      }
    },
    [activeIndex, visibleItems, handleSelect],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onKeyDown={handleKeyDown}
        className="top-[12%] left-1/2 w-[92vw] max-w-[560px] translate-x-[-50%] translate-y-0 gap-0 rounded-xl border border-border bg-background p-0 shadow-2xl sm:max-w-[560px]"
      >
        <DialogTitle className="sr-only">
          {t("searchPalette.placeholder")}
        </DialogTitle>

        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <SearchIcon
            className="size-4 shrink-0 text-muted-foreground"
            strokeWidth={1.7}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPalette.placeholder")}
            aria-label={t("searchPalette.placeholder")}
            autoFocus
            className="flex-1 bg-transparent text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden rounded-[5px] border border-border/70 bg-muted/50 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground sm:inline-block">
            ESC
          </kbd>
        </div>

        <div
          ref={listRef}
          role="listbox"
          aria-label={t("searchPalette.placeholder")}
          className="praetor-palette-list max-h-[420px] overflow-y-auto py-1"
        >
          {isLoading && visibleItems.length === 0 ? (
            <PaletteSkeletons />
          ) : visibleItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">
              {t("chatsPage.emptyTitle")}
            </div>
          ) : (
            visibleItems.map((item, i) => (
              <PaletteRow
                key={item.remoteId}
                item={item}
                index={i}
                isActive={i === activeIndex}
                locale={locale}
                query={deferredQuery}
                onMouseEnter={() => setActiveIndex(i)}
                onSelect={() => handleSelect(item)}
                fallbackTitle={t("threadList.newConversation")}
                timestampLabel={formatPaletteTimestamp(
                  item.updatedAt ?? item.createdAt,
                  t,
                )}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

type PaletteRowProps = {
  item: ThreadItem;
  index: number;
  isActive: boolean;
  locale: string;
  query: string;
  fallbackTitle: string;
  timestampLabel: string;
  onMouseEnter: () => void;
  onSelect: () => void;
};

function PaletteRow({
  item,
  index,
  isActive,
  locale,
  query,
  fallbackTitle,
  timestampLabel,
  onMouseEnter,
  onSelect,
}: PaletteRowProps) {
  const titleText = item.title?.trim() ? item.title : fallbackTitle;

  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      data-palette-row-index={index}
      onMouseEnter={onMouseEnter}
      onClick={onSelect}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left text-[13.5px] transition-colors",
        isActive ? "bg-accent text-foreground" : "text-muted-foreground",
      )}
    >
      <span className="min-w-0 flex-1 truncate text-foreground">
        <HighlightedTitle title={titleText} query={query} locale={locale} />
      </span>
      <span className="shrink-0 text-[12px] text-muted-foreground">
        {timestampLabel}
      </span>
      {isActive && (
        <kbd className="shrink-0 rounded-[5px] border border-border/70 bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          ↵
        </kbd>
      )}
    </button>
  );
}

function PaletteSkeletons() {
  return (
    <div className="flex flex-col gap-1.5 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-7 w-full animate-pulse rounded-md bg-muted/60"
          style={{ animationDelay: `${i * 90}ms` }}
        />
      ))}
    </div>
  );
}
